// Ask box (signature piece): shadcn InputGroup restyled to the Paper "Family" composer.
// `lg`: the letter composer (grows, send bottom-right); `tall` gives it room to
// write in (Today). `md`: the thread reply, one line until the text wraps, then
// it grows the same way. With a keyboard, Enter sends and Shift+Enter adds a
// line; on a touch screen return adds a line and Send sends, as in Messages.
// An `attachment` (a doc's chip, files being attached) sits above the text,
// inside the box; `tools` (an attach button) sit beside Send. `onPaste` sees a
// paste first (files pasted, slice 5b); with `onDropFiles` the box takes a
// drop of files, outlined with "Drop to attach" while one is over it. `sendable`: there's
// something to send with no words (files attached, slice 5b). With
// `stop` (`md`), an empty box's Send is Stop while a reply is being written.
// A `notice` is a strip across the box's top, inside its edge (slice 1j §6.1):
// an update in progress, or why nothing can be sent; it stays readable when
// the field below it is disabled.
import { ArrowUp, CircleAlert, RefreshCw, Square } from "lucide-react";
import {
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
  type ClipboardEvent,
  type DragEvent,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
} from "react";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupTextarea,
} from "@crew/ui/components/input-group";
import { enterSends } from "@crew/ui/lib/enter-sends";
import { cn } from "@crew/ui/lib/utils";

/** A strip across the composer's top: `updating` (muted refresh) or `locked` (an alert). */
export type ComposerNotice = { tone: "updating" | "locked"; text: ReactNode };

/** What a parent can do to the text: `put` puts words back (an unsent reply's Edit) and focuses it. */
export type ComposerHandle = { put: (text: string) => void };

type ComposerProps = {
  ref?: Ref<ComposerHandle>;
  /**
   * Clear the text the moment it's sent, without waiting on `onSubmit`: the
   * sender shows it optimistically and keeps it if it fails (a reply's "Not sent").
   */
  clearOnSubmit?: boolean;
  placeholder: string;
  /** Accessible name; defaults to the placeholder. */
  label?: string;
  size?: "lg" | "md";
  /** Left of the send button, e.g. "@ Goes to the Chief of Staff". */
  footer?: ReactNode;
  /** Return a promise to keep the text (and block sending) until it settles; a rejection keeps it. */
  onSubmit?: (text: string) => void | Promise<unknown>;
  disabled?: boolean;
  /** `lg` only: about four lines to start with, in a larger type (Today's centred ask). */
  tall?: boolean;
  /** Above the text, inside the box: e.g. an attached doc's chip with its ×. */
  attachment?: ReactNode;
  /** Beside Send, before it: an attach button (slice 5b). */
  tools?: ReactNode;
  /** There's something to send with no words: files attached (slice 5b). */
  sendable?: boolean;
  /** Sees a paste before the field: one that's files attaches them (slice 5b). */
  onPaste?: (event: ClipboardEvent<HTMLTextAreaElement>) => void;
  /** Files dropped on the box (slice 5b); without it, the box takes no drop. */
  onDropFiles?: (data: DataTransfer) => void;
  /** Send waits: files are still uploading (slice 5b). The words stay. */
  holdSend?: boolean;
  /**
   * `md` only: something can be stopped (a reply being written). While the
   * box is empty, Send is Stop; once words are typed, Send sends them.
   * `stopping`: Stop was pressed and hasn't taken yet.
   */
  stop?: { onStop: () => void; stopping: boolean };
  /** The text it starts with ("Update this doc: "); read once, when it mounts. */
  initialText?: string;
  /** Focus it when it mounts, the caret after `initialText`. */
  autoFocus?: boolean;
  /** A strip across the top, inside the box (slice 1j §6.1). */
  notice?: ComposerNotice | null;
  className?: string;
};

/** The text, sending it, and the focus it starts with. */
function useComposerText({
  ref,
  initialText = "",
  autoFocus = false,
  clearOnSubmit = false,
  sendable = false,
  holdSend = false,
  onSubmit,
}: Pick<
  ComposerProps,
  "ref" | "initialText" | "autoFocus" | "clearOnSubmit" | "sendable" | "holdSend" | "onSubmit"
>) {
  const [text, setText] = useState(initialText);
  const field = useRef<HTMLInputElement & HTMLTextAreaElement>(null);
  const focusOnMount = useRef(autoFocus);
  useEffect(() => {
    const el = field.current;
    if (!focusOnMount.current || !el) return;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
    // Only when it mounts: a later render keeps where the person put the caret.
  }, []);
  // Focused once the words have rendered: a chip put back with them remounts the field.
  const focusAfterPut = useRef(false);
  useEffect(() => {
    const el = field.current;
    if (!focusAfterPut.current || !el) return;
    focusAfterPut.current = false;
    el.focus();
    el.setSelectionRange(el.value.length, el.value.length);
  });
  useImperativeHandle(
    ref,
    () => ({
      put: (words: string) => {
        // Before a draft typed since, so neither is lost.
        setText((current) => (current.trim() ? `${words} ${current}` : words));
        focusAfterPut.current = true;
      },
    }),
    [],
  );
  const [sending, setSending] = useState(false);
  const send = () => {
    const value = text.trim();
    if ((!value && !sendable) || sending || holdSend) return;
    if (clearOnSubmit) {
      setText("");
      void Promise.resolve(value)
        .then(onSubmit)
        .catch(() => undefined);
      return;
    }
    setSending(true);
    void Promise.resolve(value)
      .then(onSubmit)
      .then(
        // Clear only what was sent: a draft typed while it was sending stays.
        () => setText((current) => (current.trim() === value ? "" : current)),
        () => undefined,
      )
      .finally(() => setSending(false));
  };
  return {
    text,
    setText,
    field,
    send,
    submit: (event: FormEvent) => {
      event.preventDefault();
      send();
    },
    onKeyDown: (event: KeyboardEvent) => {
      if (enterSends(event)) {
        event.preventDefault();
        send();
      }
    },
  };
}

const hasFiles = (event: DragEvent) => event.dataTransfer.types.includes("Files");

/** The box's drop: whether files are over it, and the form's handlers. */
function useDrop(onDropFiles: ComposerProps["onDropFiles"]) {
  const [dropping, setDropping] = useState(false);
  if (!onDropFiles) return { dropping: false, handlers: {} };
  return {
    dropping,
    handlers: {
      // Over, as it keeps firing: a leave that lost where it went (an older
      // Safari's) is undone by the next one.
      onDragOver: (event: DragEvent) => {
        if (!hasFiles(event)) return;
        event.preventDefault();
        event.dataTransfer.dropEffect = "copy";
        setDropping(true);
      },
      // Leaving the box, not moving onto a child of it.
      onDragLeave: (event: DragEvent) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDropping(false);
      },
      onDrop: (event: DragEvent) => {
        if (!hasFiles(event)) return;
        event.preventDefault();
        setDropping(false);
        onDropFiles(event.dataTransfer);
      },
    },
  };
}

/** Over the box while files are dragged over it. */
function DropCover({ radius }: { radius: string }) {
  return (
    <div
      aria-hidden
      data-composer-drop
      className={cn(
        "pointer-events-none absolute inset-0 z-10 flex items-center justify-center border-2 border-dashed border-primary bg-card/90 text-meta font-medium text-foreground",
        radius,
      )}
    >
      Drop to attach
    </div>
  );
}

function SendButton({ disabled, small }: { disabled?: boolean; small: boolean }) {
  return (
    <InputGroupButton
      type="submit"
      variant="default"
      size="icon-sm"
      aria-label="Send"
      disabled={disabled}
      className={cn("size-9 rounded-md", small ? "md:size-7" : "md:size-8")}
    >
      <ArrowUp strokeWidth={2} />
    </InputGroupButton>
  );
}

/** Stop in Send's place: the same size and spot, a filled square. */
function StopButton({ onStop, stopping }: { onStop: () => void; stopping: boolean }) {
  return (
    <InputGroupButton
      type="button"
      variant="default"
      size="icon-sm"
      aria-label={stopping ? "Stopping" : "Stop"}
      // Not `disabled`: the input group dims itself for a disabled control,
      // and the box stays open for a reply while the run stops.
      aria-disabled={stopping}
      onClick={() => {
        if (!stopping) onStop();
      }}
      className="size-9 rounded-md aria-disabled:opacity-50 md:size-7"
    >
      <Square className="size-3 fill-current" strokeWidth={0} />
    </InputGroupButton>
  );
}

/**
 * The strip: plaster ground, a hairline under it, an icon and a line of meta
 * copy. `standalone`: on its own, boxed (a form with no composer: New task).
 */
export function NoticeStrip({
  notice,
  standalone = false,
}: {
  notice: ComposerNotice;
  standalone?: boolean;
}) {
  const Icon = notice.tone === "locked" ? CircleAlert : RefreshCw;
  return (
    <div
      role="status"
      data-composer-notice={notice.tone}
      className={cn(
        "flex items-start gap-2 bg-background px-3.5 py-2 text-meta text-copy",
        standalone ? "rounded-md border border-border" : "border-b border-border",
      )}
    >
      <Icon
        aria-hidden
        strokeWidth={2.25}
        className={cn(
          "mt-0.5 size-3.5 shrink-0",
          notice.tone === "locked" ? "text-destructive" : "text-muted-foreground",
        )}
      />
      <div className="min-w-0">{notice.text}</div>
    </div>
  );
}

/**
 * With a notice, the box's edge, ground and shadow move out to a frame around
 * the strip and the field (`flat` takes them off the field's group), so the
 * strip isn't dimmed with a disabled field.
 */
function Framed({
  notice,
  radius,
  children,
}: {
  notice: ComposerNotice | null | undefined;
  radius: string;
  children: ReactNode;
}) {
  if (!notice) return <>{children}</>;
  return (
    <div className={cn("overflow-hidden bg-card shadow-composer", radius)}>
      <NoticeStrip notice={notice} />
      {children}
    </div>
  );
}

const FLAT = "rounded-none shadow-none";

const LINE_BOX =
  "rounded-tile border-0 bg-card pr-1.5 shadow-composer has-[[data-slot=input-group-control]:focus-visible]:ring-2";

/**
 * A placeholder in a one-line box stays on its line and ends in an ellipsis:
 * the box grows with typed words, never with its placeholder (iOS Safari has
 * no `field-sizing`), so a wrapped one would be cut in half. `min-w-0` keeps
 * the long line from widening the box where `field-sizing` exists.
 */
const ONE_LINE_PLACEHOLDER = "min-w-0 placeholder:truncate";

/**
 * The reply's field: one line tall until the words wrap or a new line breaks
 * it, then it grows. Past a short page it scrolls, so the thread stays.
 */
const LINE_FIELD = cn(
  "max-h-40 min-h-11 overflow-y-auto py-3 pr-2 pl-3.5 text-base leading-5 placeholder:text-hint md:text-[14px]",
  ONE_LINE_PLACEHOLDER,
);

/** `md`: the thread reply; with an attachment, a row above the text (slice 2d's doc chip). */
function LineComposer({
  state,
  name,
  placeholder,
  disabled,
  attachment,
  tools,
  sendable,
  onPaste,
  onDropFiles,
  stop,
  notice,
  className,
}: {
  state: ReturnType<typeof useComposerText>;
  name: string;
  placeholder: string;
  disabled?: boolean;
  attachment?: ReactNode;
  tools?: ReactNode;
  sendable?: boolean;
  onPaste?: ComposerProps["onPaste"];
  onDropFiles?: ComposerProps["onDropFiles"];
  stop?: ComposerProps["stop"];
  notice?: ComposerNotice | null;
  className?: string;
}) {
  const box = cn(LINE_BOX, notice && FLAT);
  const id = useId();
  const drop = useDrop(onDropFiles);
  const field = (
    <InputGroupTextarea
      ref={state.field}
      id={id}
      aria-label={name}
      placeholder={placeholder}
      value={state.text}
      onChange={(e) => state.setText(e.target.value)}
      onKeyDown={state.onKeyDown}
      onPaste={onPaste}
      disabled={disabled}
      rows={1}
      className={LINE_FIELD}
    />
  );
  const send = (
    <span className="flex items-center gap-1">
      {tools}
      {stop && !state.text.trim() && !sendable ? (
        <StopButton onStop={stop.onStop} stopping={stop.stopping} />
      ) : (
        <SendButton disabled={disabled} small />
      )}
    </span>
  );
  return (
    <form onSubmit={state.submit} {...drop.handlers} className={cn("relative", className)}>
      {drop.dropping ? <DropCover radius="rounded-tile" /> : null}
      <Framed notice={notice} radius="rounded-tile">
        {attachment ? (
          <InputGroup className={cn("h-auto flex-col items-stretch", box)}>
            <div className="flex min-w-0 px-2 pt-2">{attachment}</div>
            <div className="flex min-h-11 items-end">
              {field}
              <div className="pb-1.5">{send}</div>
            </div>
          </InputGroup>
        ) : (
          <InputGroup className={cn("h-auto min-h-11 items-end", box)}>
            {field}
            <InputGroupAddon align="inline-end">{send}</InputGroupAddon>
          </InputGroup>
        )}
      </Framed>
    </form>
  );
}

export function Composer(props: ComposerProps) {
  const state = useComposerText(props);
  const { placeholder, label, size = "lg", footer, tall = false, sendable, stop } = props;
  const box = {
    state,
    name: label ?? placeholder,
    placeholder,
    disabled: props.disabled,
    attachment: props.attachment,
    tools: props.tools,
    onPaste: props.onPaste,
    onDropFiles: props.onDropFiles,
    notice: props.notice,
    className: props.className,
  };
  if (size === "md") return <LineComposer {...box} sendable={sendable} stop={stop} />;
  return <LetterComposer {...box} footer={footer} tall={tall} />;
}

/** `lg`: the letter composer, its footer and Send below the text. */
function LetterComposer({
  state,
  name,
  placeholder,
  disabled,
  attachment,
  tools,
  onPaste,
  onDropFiles,
  notice,
  footer,
  tall,
  className,
}: {
  state: ReturnType<typeof useComposerText>;
  name: string;
  placeholder: string;
  disabled?: boolean;
  attachment?: ReactNode;
  tools?: ReactNode;
  onPaste?: ComposerProps["onPaste"];
  onDropFiles?: ComposerProps["onDropFiles"];
  notice?: ComposerNotice | null;
  footer?: ReactNode;
  tall: boolean;
  className?: string;
}) {
  const id = useId();
  const letter = LETTER[tallKey(tall)];
  const drop = useDrop(onDropFiles);
  return (
    <form onSubmit={state.submit} {...drop.handlers} className={cn("relative", className)}>
      {drop.dropping ? <DropCover radius="rounded-panel" /> : null}
      <Framed notice={notice} radius="rounded-panel">
        <InputGroup
          className={cn(
            "rounded-panel border-0 bg-card shadow-composer has-[[data-slot=input-group-control]:focus-visible]:ring-2",
            notice && FLAT,
          )}
        >
          {attachment ? (
            <InputGroupAddon align="block-start" className="px-3 pt-3 pb-0">
              {attachment}
            </InputGroupAddon>
          ) : null}
          <InputGroupTextarea
            ref={state.field}
            id={id}
            aria-label={name}
            placeholder={placeholder}
            value={state.text}
            onChange={(e) => state.setText(e.target.value)}
            onKeyDown={state.onKeyDown}
            onPaste={onPaste}
            disabled={disabled}
            rows={1}
            className={letter.area}
          />
          <InputGroupAddon align="block-end" className={letter.addon}>
            <div className="min-w-0 flex-1 text-meta text-muted-foreground">{footer}</div>
            {tools}
            <SendButton disabled={disabled} small={false} />
          </InputGroupAddon>
        </InputGroup>
      </Framed>
    </form>
  );
}

const tallKey = (tall: boolean) => (tall ? "tall" : "base");

/**
 * The letter composer's textarea and footer: taller on a phone, and `tall`
 * roomier still. A phone's box has room for a wrapped placeholder; the base
 * box is one line from `md`, so there it stays on one.
 */
const LETTER = {
  base: {
    area: "min-h-28 min-w-0 px-4 pt-3.5 text-base leading-6 placeholder:text-hint md:min-h-11 md:placeholder:truncate",
    addon: "px-4 pb-3",
  },
  tall: {
    area: "min-h-28 px-5 pt-5 text-base leading-6 placeholder:text-hint md:min-h-32 md:text-[17px] md:leading-7",
    addon: "px-5 pb-4",
  },
};
